import React, { useCallback, useEffect, useState } from 'react';
import {
  AchAvailabilityReturn,
  CounterpartyBankAccountReturn,
  MyAchPaymentReturn,
} from '../clients/rust-api-client';
import { memberAchService } from '../clients/ApiClients';
import { Button } from '../Components/Catalyst/button';
import { Badge } from '../Components/Catalyst/badge';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../Components/Catalyst/dialog';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../Components/Catalyst/fieldset';
import { Checkbox, CheckboxField } from '../Components/Catalyst/checkbox';
import { Input } from '../Components/Catalyst/input';
import { Select } from '../Components/Catalyst/select';
import { Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { useMember } from '../Hooks/MemberContext';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
} from '../utils/billingHelpers';

interface Props {
  homeId: string;
  /** What the account owes now; the amount field starts here. */
  balance?: string | null;
  onError: (message: string) => void;
}

/** The bank prints at most this many characters of the name. */
const ACH_NAME_MAX = 22;

type BadgeColor = 'amber' | 'sky' | 'green' | 'red' | 'zinc';

const statusColor = (s: string): BadgeColor => {
  switch (s) {
    case 'submitted':
      return 'amber';
    case 'processing':
      return 'sky';
    case 'paid':
      return 'green';
    case 'returned':
    case 'failed':
      return 'red';
    default:
      return 'zinc';
  }
};

const statusLabel = (s: string): string => {
  switch (s) {
    case 'submitted':
      return 'Submitted';
    case 'processing':
      return 'Processing';
    case 'paid':
      return 'Paid';
    case 'returned':
      return 'Returned';
    case 'failed':
      return 'Not accepted';
    case 'cancelled':
      return 'Cancelled';
    default:
      return s;
  }
};

const positiveMoney = (v?: string | null): string => {
  if (!v) return '';
  const n = Number(v);
  return n > 0 ? n.toFixed(2) : '';
};

/**
 * Pay by bank account.
 *
 * The homeowner keeps one bank account on file, accepts the debit
 * authorisation once, and submits a payment for the amount and date
 * they choose. **Submitting moves nothing**: the bank validates and
 * holds it, the association settles the day's batch, and the money
 * moves on the bank's next processing day. The receipt therefore says
 * "submitted", never "paid"; "paid" is what the history shows once the
 * ledger has it.
 */
const AchPayCard: React.FC<Props> = ({ homeId, balance, onError }) => {
  const { member } = useMember();
  const [availability, setAvailability] =
    useState<AchAvailabilityReturn | null>(null);
  const [history, setHistory] = useState<MyAchPaymentReturn[]>([]);
  const [loading, setLoading] = useState(true);

  // Add / replace the account
  const [editing, setEditing] = useState(false);
  const [achName, setAchName] = useState('');
  const [accountType, setAccountType] = useState('checking');
  const [routing, setRouting] = useState('');
  const [account, setAccount] = useState('');
  const [accountAgain, setAccountAgain] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Pay
  const [amount, setAmount] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [payError, setPayError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [receipt, setReceipt] = useState<MyAchPaymentReturn | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [a, h] = await Promise.all([
        memberAchService.myAchAvailability({ homeId }),
        memberAchService.listMyAchPayments({ homeId }),
      ]);
      setAvailability(a.data);
      setHistory(h.data.items);
      setEffectiveDate((d) => d || a.data.earliest_effective_date);
    } catch (err) {
      onError(
        billingErrorMessage(err, "We couldn't load your payment options.")
      );
    } finally {
      setLoading(false);
    }
  }, [homeId, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setAmount((a) => a || positiveMoney(balance));
  }, [balance]);

  const onFile: CounterpartyBankAccountReturn | null | undefined =
    availability?.bank_account;

  const openEditor = (): void => {
    const name = [member?.first_name, member?.last_name]
      .filter(Boolean)
      .join(' ');
    setAchName((onFile?.ach_name ?? name).slice(0, ACH_NAME_MAX));
    setAccountType(onFile?.account_type ?? 'checking');
    setRouting('');
    setAccount('');
    setAccountAgain('');
    setAccepted(false);
    setFormError(null);
    setEditing(true);
  };

  const save = async (): Promise<void> => {
    if (!availability) return;
    const name = achName.trim();
    if (name.length === 0 || name.length > ACH_NAME_MAX) {
      setFormError(
        `The name on the account is 1 to ${ACH_NAME_MAX} characters.`
      );
      return;
    }
    if (!/^\d{9}$/.test(routing.replace(/\s/g, ''))) {
      setFormError('The routing number is the nine digits on your check.');
      return;
    }
    if (account.replace(/\s/g, '') !== accountAgain.replace(/\s/g, '')) {
      setFormError('The account numbers do not match.');
      return;
    }
    if (!accepted) {
      setFormError('Please read and accept the authorization.');
      return;
    }
    try {
      setSaving(true);
      setFormError(null);
      await memberAchService.setMyBankAccount({
        homeId,
        homeownerBankAccountUpsert: {
          ach_name: name,
          account_type: accountType,
          routing_number: routing.replace(/\s/g, ''),
          account_number: account.replace(/\s/g, ''),
          authorization_accepted: true,
          authorization_version: availability.authorization_version,
        },
      });
      setEditing(false);
      setAccount('');
      setAccountAgain('');
      await load();
    } catch (err) {
      setFormError(
        billingErrorMessage(err, "We couldn't save your bank account.")
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (): Promise<void> => {
    try {
      setSaving(true);
      await memberAchService.removeMyBankAccount();
      setConfirmRemove(false);
      await load();
    } catch (err) {
      onError(
        billingErrorMessage(err, "We couldn't remove your bank account.")
      );
      setConfirmRemove(false);
    } finally {
      setSaving(false);
    }
  };

  const submit = async (): Promise<void> => {
    const n = Number(amount);
    if (
      !amount ||
      isNaN(n) ||
      n <= 0 ||
      !/^\d+(\.\d{1,2})?$/.test(amount.trim())
    ) {
      setPayError('Enter an amount in dollars and cents, like 250.00.');
      return;
    }
    if (!effectiveDate) {
      setPayError('Choose the date to pay on.');
      return;
    }
    try {
      setPaying(true);
      setPayError(null);
      const { data } = await memberAchService.submitMyAchPayment({
        homeId,
        achPaymentCreate: {
          amount: amount.trim(),
          effective_date: effectiveDate,
        },
      });
      setReceipt(data);
      setHistory((h) => [data, ...h]);
      if (data.status === 'failed') {
        setPayError(data.message ?? 'The bank did not accept the payment.');
      }
    } catch (err) {
      setPayError(billingErrorMessage(err, "We couldn't submit your payment."));
    } finally {
      setPaying(false);
    }
  };

  if (loading || !availability) return null;
  if (!availability.enabled) return null;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-700 dark:bg-gray-800">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Subheading>Pay from your bank account</Subheading>
          <Text className="mt-1 text-sm">
            No fee. Your association settles payments once a day, and the money
            leaves your account on the bank&rsquo;s next processing day after
            that.
          </Text>
        </div>
      </div>

      {onFile ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-zinc-50 px-4 py-3 dark:bg-zinc-900/40">
          <div>
            <Text className="text-sm font-medium text-zinc-900 dark:text-white">
              {onFile.ach_name}
            </Text>
            <Text className="text-sm">
              {onFile.account_type} ending in {onFile.account_last4} · routing{' '}
              {onFile.routing_number}
            </Text>
          </div>
          <div className="flex gap-2">
            <Button plain onClick={() => setConfirmRemove(true)}>
              Remove
            </Button>
            <Button outline onClick={openEditor}>
              Replace
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <Button color="sky" onClick={openEditor}>
            Add a bank account
          </Button>
        </div>
      )}

      {onFile && (
        <div className="mt-5">
          {payError && (
            <ErrorAlert
              className="mb-3"
              title="Not submitted"
              onDismiss={() => setPayError(null)}
            >
              {payError}
            </ErrorAlert>
          )}
          {receipt && receipt.status !== 'failed' ? (
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm dark:border-green-900 dark:bg-green-950/40">
              <p className="font-medium text-green-800 dark:text-green-300">
                Payment of {formatMoney(receipt.amount)} submitted
              </p>
              <p className="mt-1 text-green-800/80 dark:text-green-300/80">
                Your account will be debited on or after{' '}
                {formatBillingDate(receipt.effective_date)}
                {receipt.bank_reference
                  ? ` · bank reference ${receipt.bank_reference}`
                  : ''}
                . It shows below as Processing once the association settles it,
                and as Paid once it posts to your account.
              </p>
              <Button plain className="mt-2" onClick={() => setReceipt(null)}>
                Make another payment
              </Button>
            </div>
          ) : (
            <Fieldset>
              <FieldGroup>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field>
                    <Label>Amount (USD)</Label>
                    <Input
                      name="amount"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                    {balance && Number(balance) > 0 && (
                      <Description>
                        You owe {formatMoney(balance)}. You may pay more.
                      </Description>
                    )}
                  </Field>
                  <Field>
                    <Label>Pay on</Label>
                    <Input
                      name="effective_date"
                      type="date"
                      min={availability.earliest_effective_date}
                      value={effectiveDate}
                      onChange={(e) => setEffectiveDate(e.target.value)}
                    />
                    <Description>
                      Your account is debited on or after this date.
                    </Description>
                  </Field>
                </div>
                <Button
                  color="sky"
                  onClick={submit}
                  disabled={paying}
                  className="w-full sm:w-auto"
                >
                  {paying
                    ? 'Submitting…'
                    : `Pay ${amount && Number(amount) > 0 ? formatMoney(Number(amount).toFixed(2)) : ''}`.trim()}
                </Button>
              </FieldGroup>
            </Fieldset>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-6">
          <Text className="text-sm font-medium text-zinc-900 dark:text-white">
            Bank account payments
          </Text>
          <ul className="mt-2 divide-y divide-zinc-100 dark:divide-zinc-700/60">
            {history.slice(0, 6).map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <span className="text-zinc-900 dark:text-white">
                    {formatBillingDate(p.effective_date)}
                  </span>
                  {p.message && <Text className="text-xs">{p.message}</Text>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="tabular-nums text-zinc-900 dark:text-white">
                    {formatMoney(p.amount)}
                  </span>
                  <Badge color={statusColor(p.status)}>
                    {statusLabel(p.status)}
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog
        open={editing}
        onClose={saving ? () => undefined : () => setEditing(false)}
      >
        <DialogTitle>
          {onFile ? 'Replace your bank account' : 'Add a bank account'}
        </DialogTitle>
        <DialogDescription>
          Your account number is stored encrypted and never shown again beyond
          its last four digits.
        </DialogDescription>
        <DialogBody>
          {formError && (
            <ErrorAlert
              className="mb-4"
              title="Not saved"
              onDismiss={() => setFormError(null)}
            >
              {formError}
            </ErrorAlert>
          )}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name on the account</Label>
                <Input
                  name="ach_name"
                  maxLength={ACH_NAME_MAX}
                  value={achName}
                  onChange={(e) => setAchName(e.target.value)}
                />
                <Description>
                  As it appears at your bank. {achName.trim().length}/
                  {ACH_NAME_MAX}
                </Description>
              </Field>
              <Field>
                <Label>Account type</Label>
                <Select
                  name="account_type"
                  value={accountType}
                  onChange={(e) => setAccountType(e.target.value)}
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </Select>
              </Field>
              <Field>
                <Label>Routing number</Label>
                <Input
                  name="routing_number"
                  inputMode="numeric"
                  autoComplete="off"
                  value={routing}
                  onChange={(e) => setRouting(e.target.value)}
                />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Account number</Label>
                  <Input
                    name="account_number"
                    inputMode="numeric"
                    autoComplete="off"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Account number, again</Label>
                  <Input
                    name="account_number_again"
                    inputMode="numeric"
                    autoComplete="off"
                    value={accountAgain}
                    onChange={(e) => setAccountAgain(e.target.value)}
                  />
                </Field>
              </div>
              <CheckboxField>
                <Checkbox
                  name="authorization"
                  checked={accepted}
                  onChange={setAccepted}
                />
                <Label>I authorize these payments</Label>
                <Description>{availability.authorization_text}</Description>
              </CheckboxField>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save bank account'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRemove} onClose={() => setConfirmRemove(false)}>
        <DialogTitle>Remove your bank account?</DialogTitle>
        <DialogDescription>
          Payments you have already submitted are not affected. You can add an
          account again at any time.
        </DialogDescription>
        <DialogActions>
          <Button
            plain
            onClick={() => setConfirmRemove(false)}
            disabled={saving}
          >
            Keep it
          </Button>
          <Button color="red" onClick={remove} disabled={saving}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AchPayCard;
